/**
 * This file provides a compatibility layer for code that depends on the old firebase.ts structure
 * It maintains the same API but internally uses the new lazy-loaded firebase.ts module
 */

import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { initializeFirestore } from 'firebase/firestore'
import { getStorage as firebaseGetStorage } from 'firebase/storage'
import { WizardType } from 'src/context/WizardContext'
import { AccessLevelEnum, BASE_AUTH_URL } from 'src/utils/config/config'
import { AccountingProviderName } from './openApiV2'

// Firebase configuration
export const firebaseConfig: FirebaseOptions = {
  apiKey: 'AIzaSyAPstTsXFKtlRyC5XnfE71A1PBwQGPhuNY',
  authDomain: BASE_AUTH_URL,
  projectId: 'arboreal-vision-339901',
  storageBucket: 'arboreal-vision-339901.appspot.com',
  messagingSenderId: '983150008719',
  appId: '1:983150008719:web:094b902dc0e51ab27dd3fa'
}

// Initialize the app
const app: FirebaseApp = initializeApp(firebaseConfig)

// Initialize services synchronously for compatibility
const auth = getAuth(app)
const db = initializeFirestore(app, {})
const firebaseDb = db // for backward compatibility
const storage = firebaseGetStorage(app)

// Export the initialized instances
export { app, auth, db, firebaseDb, storage }

/**
 * Initialize Firebase app only once (for dynamic imports)
 * This ensures that the app is only initialized once, even if multiple components try to initialize it.
 */
let dynamicApp: FirebaseApp | null = null

export const getFirebaseApp = (): FirebaseApp => {
  if (!dynamicApp) {
    dynamicApp = app
  }
  return dynamicApp
}

// Firestore service - lazy loaded
export const getFirestore = async () => {
  return db
}

// Storage service - lazy loaded
export const getFirebaseStorage = async () => {
  return storage
}

// For backward compatibility, initialize the storage lazily
export const getStorage = async () => {
  return await getFirebaseStorage()
}

export const bookkeepingIntegrationsCollectionIds = Object.values(AccountingProviderName).map((key) => `${key}_integration_details`)

export const allowedCollectionsWithoutId = ['route_categories', 'subchain_modules']
export const allowedCollectionsWithDocumentId = [
  'org_config_2',
  'panel_settings',
  'notes',
  'saved_searches',
  'store_details',
  'slack_teams_info',
  'users',
  'configs'
].concat(bookkeepingIntegrationsCollectionIds)

export const fetchFirebaseData = async (collectionName: string, documentId?: string): Promise<any> => {
  const { collection, getDoc, getDocs, doc } = await import('firebase/firestore')
  const db = await getFirestore()

  const firebaseCall = async (): Promise<any> => {
    if (allowedCollectionsWithDocumentId.includes(collectionName) && documentId) {
      const res = await getDoc(doc(db, collectionName, documentId))
      return res.data()
    }

    if (allowedCollectionsWithoutId.includes(collectionName)) {
      const res = await getDocs(collection(db, collectionName))
      const documents = res.docs.map((doc) => doc.data())
      return documents
    }
  }

  return await firebaseCall()
}

export const fetchRouteCategories = async (): Promise<any> => {
  try {
    const response = await fetchFirebaseData('route_categories')
    return response
  } catch (error) {
    return Promise.reject(error)
  }
}

export const fetchUserOrgAndRole = async (id: string): Promise<any> => {
  try {
    const response = await fetchFirebaseData('users', id)
    return response
  } catch (error) {
    return Promise.reject(error)
  }
}

export const fetchCredentials = async (org): Promise<any> => {
  try {
    const { collection, getDocs, query, where } = await import('firebase/firestore')
    const db = await getFirestore()
    const Doc = query(collection(db, 'credentials_v2'), where('chain', '==', org))
    const querySnapshot = await getDocs(Doc)
    return querySnapshot
  } catch (error) {
    return Promise.reject(error)
  }
}

export const savePanelSettings = async (_org, _panelSettings): Promise<void> => {
  try {
  } catch (error) {
    return Promise.reject(error)
  }
}

export const fetchPanelSettings = async (org): Promise<any> => {
  try {
    const { doc, getDoc } = await import('firebase/firestore')
    const db = await getFirestore()
    const querySnapshot = await getDoc(doc(db, 'panel_settings', org))
    return querySnapshot.data()
  } catch (error) {
    return Promise.reject(error)
  }
}

export const fetchPanels = async (): Promise<any> => {
  try {
    const { collection, getDocs, query } = await import('firebase/firestore')
    const db = await getFirestore()
    const querySnapshot = await getDocs(query(collection(db, 'panels')))

    const documents = querySnapshot.docs.map((doc) => doc.data())
    return documents
  } catch (error) {
    return Promise.reject(error)
  }
}

export const fetchOrgConfig = async (org: string, access_level: string | string[]): Promise<any> => {
  const admin = AccessLevelEnum.BUSINESS_ADMIN
  try {
    const response = await fetchFirebaseData('org_config_2', org.trim())
    let tempAcessLevel = []

    // this will check if access level is string (old version) type
    if (access_level) {
      if (typeof access_level === 'string') {
        tempAcessLevel = [access_level]
      } else tempAcessLevel = access_level
    } else {
      tempAcessLevel = [admin]
    }

    // this will join routes for multiple access levels
    const tempAccessLevels: string[] = Object.keys(response.access_levels || {})
    let routes: string[] = []

    // this will run the joining of routes if user is not business-admin
    if (!tempAccessLevels.includes(admin)) {
      tempAcessLevel.forEach((item) => {
        routes = routes.concat(response.access_levels ? response.access_levels[item]?.routes || [] : [])
      })
    } else {
      routes = response.access_levels ? response.access_levels[admin]?.routes || [] : []
    }
    return {
      ...response,
      navConfig: routes,
      filterConfig: response.filterConfig || {
        am_name: false,
        vb_name: false,
        vb_platform: true,
        b_name: true
      }
    }
  } catch (error) {
    return Promise.reject(error)
  }
}

export const setAllAccessLevels = async (): Promise<AccessLevelType[]> => {
  try {
    const { collection, getDocs } = await import('firebase/firestore')
    const db = await getFirestore()
    const querySnapshot = await getDocs(collection(db, 'access_levels'))
    const documents = querySnapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        name: data?.name,
        label: data?.label,
        is_custom: data?.is_custom
      }
    })
    localStorage.setItem('all_access_levels', JSON.stringify(documents))
    return documents
  } catch (err) {
    return Promise.reject(err)
  }
}
type AccessLevelType = { name: string; label: string; is_custom?: boolean }
export function getAllAccessLevels(): AccessLevelType[]
export function getAllAccessLevels(name: string): AccessLevelType
export function getAllAccessLevels(name?: string): any {
  let result: AccessLevelType[] = []
  try {
    result = JSON.parse(localStorage.getItem('all_access_levels') || '[]')
  } catch (error) {
    console.error('Error parsing access levels from local storage:', error)
  }

  if (name) {
    return result.find((item) => item.name === name)
  }
  return result
}

export const saveSearch = async (_savedSearch): Promise<void> => {
  try {
  } catch (error) {
    console.error('Failed to save search:', error)
  }
}

export const fetchSavedSearches = async (user): Promise<any> => {
  try {
    const { collection, getDocs, query, where } = await import('firebase/firestore')
    const db = await getFirestore()
    const querySnapshot = await getDocs(query(collection(db, 'saved_searches'), where('createdBy.org', '==', user.org)))
    const documents = querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id
    }))
    const result = (documents as any).filter((document) => document.createdBy.uid === user.uid)
    result.sort((a, b) => b.updatedAt - a.updatedAt)
    return result
  } catch (error) {
    return Promise.reject(error)
  }
}

export const deleteSavedSearch = async (id: string): Promise<void> => {
  try {
    const { deleteDoc, doc } = await import('firebase/firestore')
    const db = await getFirestore()
    await deleteDoc(doc(db, 'saved_searches', id))
  } catch (error) {
    return Promise.reject(error)
  }
}

export const updateSavedSearch = async (_id, _savedSearch): Promise<void> => {
  try {
  } catch (error) {
    return Promise.reject(error)
  }
}

export const getStoreDetails = async (org: string): Promise<any> => {
  try {
    const { collection, getDocs, query, where } = await import('firebase/firestore')
    const db = await getFirestore()
    const querySnapshot = await getDocs(query(collection(db, 'store_details'), where('chain', '==', org)))
    const result = querySnapshot.docs.map((doc) => doc.data())
    return result
  } catch (err) {
    return Promise.reject(err)
  }
}

export const isSlackIntegrated = async (org: string): Promise<boolean> => {
  try {
    const { collection, getDocs, query, where } = await import('firebase/firestore')
    const db = await getFirestore()
    const querySnapshot = await getDocs(query(collection(db, 'slack_teams_info'), where('chain', '==', org)))
    return !querySnapshot.empty
  } catch (err) {
    return Promise.reject(err)
  }
}

export function logTimeDifference(end: number, start: number, title?: string): void {
  const diff = end - start
  if (diff > 400) console.log(`${title} - ${parseFloat((diff / 1000).toString()).toFixed(2)}s`)
}
export let labelList = []
export let timeList = []
export function appendLatestTime(label: string): void {
  const end = Date.now()
  timeList.push(end)
  labelList.push(label)
  if (timeList.length > 1) {
    logTimeDifference(end, timeList[timeList.length - 2], `${labelList[labelList.length - 2]} - ${label}`)
    logTimeDifference(timeList[timeList.length - 1], timeList[0], 'TOTAL')
  }
  // if (timeList.length > 10) console.log({timeList, labelList})
}

export function clearList(): void {
  labelList = []
  timeList = []
}

export function setTokenExpireToStorage(expirationTime: string): void {
  sessionStorage.setItem('token_expiration_time', expirationTime)
}

export function getTokenExpireFromStorage(): string | null {
  return sessionStorage.getItem('token_expiration_time')
}

export function setTokenCreationToStorage(creationTime: string): void {
  sessionStorage.setItem('token_creation_time', creationTime)
}

export function getTokenCreationFromStorage(): string | null {
  return sessionStorage.getItem('token_creation_time')
}

export async function getWizardRoutes(wizardUid: string[]): Promise<WizardType[]> {
  if (!wizardUid.length) return []
  const { collection, getDocs, query, where } = await import('firebase/firestore')
  const db = await getFirestore()
  const wizardConfigRef = collection(db, 'wizard_config')
  const q = query(wizardConfigRef, where('key', 'in', wizardUid))
  const querySnapshot = await getDocs(q)
  return querySnapshot.docs.map((doc) => doc.data() as WizardType)
}
